/* eslint-disable array-callback-return */
import React, { useEffect, useState } from 'react';
import { FiveTabWidget } from '@sde/widgets-core/src/widgets/FiveTab';
import { useUser } from '@sde/widgets-core/src/redux/resources/user';
import { useHeaderMenu } from 'src/state/headerMenu';
import { styled } from '@uhg-abyss/web/tools/styled';
import PropTypes from 'prop-types';
import { validatePermissions } from '@sde/widgets-core/src/redux/resources/user/helpers';
import { UHCUpdatesWidget } from '@sde/widgets-core/src/widgets/UHCUpdates';
import { useTabs } from '@sde/widgets-core/src/redux/resources/tabs';
import { storage } from '@uhg-abyss/web/tools/storage';
import { Modal } from '@uhg-abyss/web/ui/Modal';
import { Flex as AbyssFlex } from '@uhg-abyss/web/ui/Flex';
import { IconBrand } from '@uhg-abyss/web/ui/IconBrand';
import { Box } from '@uhg-abyss/web/ui/Box';
import { useModal } from 'src/state/modal';
import { useProvider } from 'src/state/provider';
import { useGetRequestGuid } from '@sde/widgets-core/src/common/hooks/useGetRequestGuidQuery';
import { getFeature } from '@sde/widgets-core/src/redux/tools/features';
import { usePPDLagoon } from '@sde/widgets-core/src/tools/lagoon';
import { ClaimsTab, claimsRightRails } from './ClaimsTab';
import { ReferralsTab, referralsRightRail } from './ReferralsTab';
import { EligibilitySearch, EligibilityRightRails } from './EligibilitySearch';
import { DocumentsTab, documentsRightRail } from './DocumentsTab';
import { PriorAuthTab, PaanRightRails } from './PriorAuthTab';
import { ActionRequiredWidget } from './ActionRequiredWidget';
import { useActionRequiredFilter } from 'src/state/actionRequiredFilter';
import { usePayer } from '@src/state/payer';
import { useDashboard } from '@src/state/dashboard';
import { usePayers } from '@sde/widgets-core/src/redux/resources/payers';

const PaddedDiv = styled('div', {
  paddingBottom: '25px',
});

const ErrorText = styled('div', {
  margin: '30px 0',
  color: '$primary1',
  textAlign: 'center',
  padding: '0 20px',
});

export const FiveTab = ({
  widgetTabsOrder,
  breakPoint,
  actionRequiredCount,
  setActionRequiredCount,
}) => {
  const { provider } = useProvider.state();
  const { user } = useUser.state();
  const { payer } = usePayer.state();
  const { dashboard, providerPreferenceComplete } = useDashboard.state();
  const { getPayers } = usePayers.actions();
  const { payers } = usePayers.state();
  const { toggleModal, modals, modalData } = useModal.actions();
  const { toggleProvider } = useHeaderMenu.actions();
  const { setActionRequiredFilter } = useActionRequiredFilter.actions();
  const { actionRequiredFilter } = useActionRequiredFilter.state();
  const [permissionsContext, setTabPermissionsContext] = useState('');
  const [filteredWidgetTabs, setFilteredWidgetTabs] = useState([]);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [actionRequiredCallsCompleted, setActionRequiredCallsCompleted] =
    useState(false);
  // const [actionRequiredCount, setActionRequiredCount] = useState(0);
  const DEFAULT_ERROR = `The system is unable to respond at the moment. \
  Please try refreshing the page (click on REFRESH at the top of the page \
  or press F5) or try again at a later time.`;
  const { tabIndex } = useTabs.state();
  const { setTabIndex, setVisibleTabs } = useTabs.actions();
  const [guid, setGuid] = useState(null);
  const [, getRequestGuid] = useGetRequestGuid({
    onCompleted: (response) => {
      setGuid(response.requestContextGUID);
    },
    onError: (err) => {
      setGuid(null);
    },
  });

  const { getMessage } = usePPDLagoon();

  const appPermissions = {
    claims: validatePermissions(user, 'claims.read'),
    eligibility: validatePermissions(user, 'eligibility.read'),
    referrals: validatePermissions(user, 'referrals.read'),
    paan: validatePermissions(user, 'prior-auth.read'),
  };
  const widgetTabsKey = {
    actionRequired: {
      name: 'actionRequired',
      label: 'Action Required',
      iconName: 'star',
      component: (
        <ActionRequiredWidget
          user={user}
          provider={provider}
          actionRequiredFilter={actionRequiredFilter}
          setActionRequiredCount={setActionRequiredCount}
          setActionRequiredCallsCompleted={setActionRequiredCallsCompleted}
          setActionRequiredFilter={setActionRequiredFilter}
        />
      ),
      permissions: true,
      labelCount: actionRequiredCount,
    },
    eligibility: {
      name: 'eligibility',
      label: 'Eligibility',
      iconName: 'umbrella',
      component: (
        <EligibilitySearch
          user={user}
          toggleModal={toggleModal}
          provider={provider}
          payer={payer}
          payers={payers}
          modalData={modalData}
          modals={modals}
          dashboard={dashboard}
          providerPreferenceComplete={providerPreferenceComplete}
        />
      ),
      rightRail: EligibilityRightRails(user, toggleModal, dashboard),
      permissions: appPermissions.eligibility,
    },
    claims: {
      name: 'claims',
      label: 'Claims & Payments',
      iconName: 'cost',
      component: (
        <ClaimsTab
          user={user}
          setIsErrorModalOpen={setIsErrorModalOpen}
          toggleModal={toggleModal}
          provider={provider}
          payer={payer}
          payers={payers}
        />
      ),
      rightRail: claimsRightRails(user, setIsErrorModalOpen, toggleModal),
      permissions: appPermissions.claims,
    },
    priorAuth: {
      name: 'priorAuth',
      label: 'Prior Authorizations & Notifications',
      iconName: 'clipboard',
      component: (
        <PriorAuthTab
          user={user}
          toggleProvider={toggleProvider}
          toggleModal={toggleModal}
          guid={guid}
        />
      ),
      permissions: appPermissions.paan,
      rightRail: PaanRightRails(user, toggleModal, guid),
    },
    referrals: {
      name: 'referrals',
      label: 'Referrals',
      iconName: 'network',
      component: (
        <ReferralsTab
          user={user}
          toggleModal={toggleModal}
          provider={provider}
          payer={payer}
          payers={payers}
          modalData={modalData}
          modals={modals}
          dashboard={dashboard}
          providerPreferenceComplete={providerPreferenceComplete}
        />
      ),
      rightRail: referralsRightRail(user, toggleModal),
      permissions: appPermissions.referrals,
    },
    doclib: {
      name: 'doclib',
      label: 'Documents & Reporting',
      iconName: 'document_multiple',
      component: (
        <DocumentsTab
          user={user}
          provider={provider}
          payer={payer}
          toggleModal={toggleModal}
        />
      ),
      rightRail: documentsRightRail(user, toggleModal),
      permissions: true,
    },
    uhcUpdates: {
      name: 'uhcUpdates',
      label: 'UnitedHealthcare Updates',
      iconName: 'calendar',
      component: <UHCUpdatesWidget />,
      permissions: true,
      toggle: 'PROVIDER_PORTAL_UPDATES',
      subText: `Updated ${getMessage('uhc-updates-date')}`,
    },
  };

  const getTabPermissionsContext = () => {
    const withPermissions = [];
    const noPermissions = [];

    filteredWidgetTabs.forEach((tab) => {
      if (tab.permissions === true) {
        withPermissions.push(tab.name);
      } else {
        noPermissions.push(tab.name);
      }
    });
    const noPermissionsText =
      noPermissions.length > 0 ? `but not ${noPermissions.join(' and ')}` : '';
    return `Access to ${withPermissions.join(' and ')} ${noPermissionsText}`;
  };

  const featureToggleCheck = (tab) => {
    return tab.toggle ? getFeature(tab.toggle) : true;
  };

  useEffect(() => {
    if (user && user.uuid && getFeature('NEW_PEER_TO_PEER_LINK')) {
      getRequestGuid({
        variables: {
          uuid: user.uuid,
        },
      });
    }
  }, [user]);

  useEffect(() => {
    getPayers();
  }, []);

  useEffect(() => {
    if (actionRequiredCallsCompleted) {
      widgetTabsKey.actionRequired.hasLabelCount = true;
    }

    const updatedTabs = widgetTabsOrder
      .map((tab) => widgetTabsKey[tab])
      .filter((mappedTab) =>
        mappedTab?.permissions === true ? featureToggleCheck(mappedTab) : false
      );
    const storedTab = storage.session.get('selectedTab');
    const newTabIndex = updatedTabs.findIndex((element) => {
      return element.name === storedTab;
    });
    if (storedTab && widgetTabsOrder.length > 0) {
      setTabIndex(newTabIndex >= 0 ? newTabIndex : 0);
    }
    setFilteredWidgetTabs(updatedTabs);
  }, [
    appPermissions.claims,
    appPermissions.eligibility,
    appPermissions.referrals,
    widgetTabsOrder,
    actionRequiredCount,
    actionRequiredCallsCompleted,
    payer.payerId,
  ]);

  useEffect(() => {
    if (filteredWidgetTabs.length > 0) {
      setTabPermissionsContext(getTabPermissionsContext());
    }
    setVisibleTabs(filteredWidgetTabs.map((tab) => tab.name));
  }, [filteredWidgetTabs]);

  useEffect(() => {
    if (filteredWidgetTabs.length > 0 && tabIndex >= 0) {
      storage.session.set('selectedTab', filteredWidgetTabs[tabIndex]?.name);
    }
  }, [tabIndex, filteredWidgetTabs]);

  const widgetProps = {
    data: {
      tabIndex,
      setTabIndex,
      user,
      widgetTabs: filteredWidgetTabs,
      permissionsContext,
      breakPoint,
    },
  };
  return (
    <PaddedDiv>
      <div data-testid="fiveTab-wrapper">
        <FiveTabWidget {...widgetProps} />
      </div>
      <Modal isOpen={isErrorModalOpen} hideClose={true} offset={280}>
        <Modal.Section>
          <Box color="$white" data-testid="error-modal-box">
            <AbyssFlex
              justify="center"
              alignItems="center"
              alignContent="center"
              data-testid="error-modal"
            >
              <AbyssFlex.Content>
                <IconBrand size="48px" icon="alert" brand="uhc" />
              </AbyssFlex.Content>
              <AbyssFlex.Content>
                <ErrorText>
                  <h2 data-uhc-brand="font--heading color--blue">
                    {DEFAULT_ERROR}
                  </h2>
                </ErrorText>
              </AbyssFlex.Content>
            </AbyssFlex>
          </Box>
        </Modal.Section>
      </Modal>
    </PaddedDiv>
  );
};

FiveTab.propTypes = {
  widgetTabsOrder: PropTypes.arrayOf(PropTypes.string),
  breakPoint: PropTypes.number.isRequired,
  actionRequiredCount: PropTypes.number,
  setActionRequiredCount: PropTypes.func,
};

FiveTab.defaultProps = {
  widgetTabsOrder: [],
  actionRequiredCount: 0,
  setActionRequiredCount: () => {},
};
